import {
  COMMON_PLOT_CONFIG,
  HIPHEN_GREEN,
  MODALITY_PROPERTY,
  THEME,
} from "../../../constants";
import { slideExportConfig, traitLabel } from "./slideExport";
import { useCallback, useMemo, useState } from "react";

import { Chart } from "./Chart";
import Plot from "react-plotly.js";
import PropTypes from "prop-types";
import { PropertyName } from "../../../components/PropertyName/PropertyName";
import { PropertySelect } from "../../../components/PropertySelect/PropertySelect";
import ReactSwitch from "react-switch";
import { SelectionFloatingCard } from "./ComparativeScatter/SelectionFloatingCard/SelectionFloatingCard";
import { selectFilteredFeatures } from "../../../selectors/resultMap";
import { useAttributeColor } from "../../../hooks/useAttributeColor";
import { useSelector } from "react-redux";

const PLOT_STYLE = {
  width: "100%",
  height: "100%",
};

export const ModalityProfiler = ({ trait, exportName }) => {
  const features = useSelector(selectFilteredFeatures);
  const [modalities, attributes] = useSelector(({ resultMap }) => [
    resultMap.distinctModalities,
    resultMap.attributes,
  ]);
  const [selectedPlots, setSelectedPlots] = useState([]);
  const [selectedAttribute, setSelectedAttribute] = useState(MODALITY_PROPERTY);
  const [showOutliers, setShowOutliers] = useState(true);

  const data = useMemo(
    () => ({
      [MODALITY_PROPERTY]: modalities,
      ...attributes,
    }),
    [attributes, modalities]
  );

  // selectedAttribute is local state that survives trial/site changes; when the
  // new site lacks that column (or it was cleared to null), fall back to
  // modality instead of indexing an undefined key.
  const activeAttribute = data[selectedAttribute]
    ? selectedAttribute
    : MODALITY_PROPERTY;

  // Boxes are grouped by activeAttribute, so the colours must come from the same
  // attribute or the chart stops matching the map.
  const getColor = useAttributeColor(activeAttribute);

  const displayData = useMemo(() => {
    // Groups and plots must use the same key: a plot whose value is missing,
    // empty or absent from the distinct list (number, unknown modality) used
    // to hit an undefined group and crash the dashboard.
    const groupKey = (value) => value ?? "";
    const modalitiesData = {};
    data[activeAttribute].forEach((modality) => {
      modalitiesData[groupKey(modality)] = [];
    });

    features.forEach((feature) => {
      const key = groupKey(feature.properties[activeAttribute]);
      (modalitiesData[key] ??= []).push({
        id: feature.id,
        value: feature.properties[trait.technical_name],
      });
    });

    return Object.entries(modalitiesData).map(([modality, plots]) => ({
      y: plots.map(({ value }) => value),
      meta: plots.map(({ id }) => id),
      type: "box",
      boxpoints: showOutliers,
      name: modality,
      marker: {
        color: modality ? getColor(modality) : HIPHEN_GREEN,
      },
    }));
  }, [
    data,
    features,
    activeAttribute,
    trait.technical_name,
    showOutliers,
    getColor,
  ]);

  const layout = useMemo(
    () => ({
      autosize: true,
      margin: { r: 30, t: 0, b: 25 },
      dragmode: "select",
      plot_bgcolor: "transparent",
      paper_bgcolor: "white",
      yaxis: {
        gridcolor: THEME.indicators,
        tickfont: { size: 11.5 },
        fixedrange: true,
        automargin: true,
      },
      xaxis: {
        automargin: true,
        gridcolor: THEME.indicators,
        tickfont: { size: 11.5 },
        fixedrange: true,
        type: "category",
      },
      font: { color: THEME.indicators, size: 13 },
      showlegend: false,
    }),
    []
  );

  const config = useMemo(
    () => ({
      ...COMMON_PLOT_CONFIG,
      ...slideExportConfig({
        title: `Performance of attribute ${activeAttribute} on ${traitLabel(
          trait
        )}`,
        filename: `${activeAttribute}_${exportName}`,
      }),
    }),
    [exportName, activeAttribute, trait]
  );

  // Handle selection of data points in the plot
  const handleSelection = useCallback(
    (event) => {
      if (!event) return;
      if (event.points.length === 0) return;
      const selectedFeatures = event.points.map((point) =>
        features.find(({ id }) => id === point.meta)
      );
      setSelectedPlots(selectedFeatures);
    },
    [features]
  );

  return (
    <Chart
      icon="fa-solid fa-tag"
      title={
        <span>
          Performance of attribute{" "}
          <PropertyName showIcon technicalName={activeAttribute} /> on{" "}
          <PropertyName
            className="breeder-selected-trait"
            showIcon
            showUnit
            technicalName={trait.technical_name}
          />
        </span>
      }
    >
      <div className="position-relative">
        <SelectionFloatingCard plots={selectedPlots} />
      </div>
      <Plot
        useResizeHandler
        data={displayData}
        onSelected={handleSelection}
        layout={layout}
        config={config}
        style={PLOT_STYLE}
      />

      <div className="d-flex justify-content-between">
        <div className="d-flex gap-2 align-items-center">
          <ReactSwitch
            onChange={setShowOutliers}
            checked={showOutliers}
            height={20}
            width={40}
            uncheckedIcon={null}
            checkedIcon={null}
            onColor={HIPHEN_GREEN}
          />
          Outliers points
        </div>
        <PropertySelect
          filterFn={(key) => Object.keys(data).includes(key)}
          className="w-50"
          placeholder="Attribute"
          value={activeAttribute}
          menuPlacement="top"
          onChange={(value) => setSelectedAttribute(value ? value.value : null)}
        />
      </div>
    </Chart>
  );
};

ModalityProfiler.propTypes = {
  trait: PropTypes.object.isRequired,
  exportName: PropTypes.string.isRequired,
};
